import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type MutableRefObject } from 'react'
import { CalendarClock } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  agendarFieldIconWrapClass,
  agendarFieldLabelClass,
  agendarFieldShellClass,
} from './agendarReuniaoFieldStyles'
import { MeetingDatetimePicker } from './MeetingDatetimePicker'
import { parseDatetimeLocal } from './meetingDatetimeValue'

function formatDisplay(value: string, dateOnly: boolean) {
  if (!value) return ''
  if (dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }
  const date = parseDatetimeLocal(value)
  if (!date) return value
  return date.toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type DatetimeLocalFieldProps = {
  value: string
  onChange: (value: string) => void
  inputRef?: MutableRefObject<HTMLInputElement | null>
  label?: string
  placeholder?: string
  highlight?: boolean
  attention?: boolean
  min?: string
  openOnMount?: boolean
  dateOnly?: boolean
  'aria-invalid'?: boolean
}

export function DatetimeLocalField({
  value,
  onChange,
  inputRef: externalRef,
  label = 'Data e hora',
  placeholder = 'Toque para escolher data e hora',
  highlight = false,
  attention = false,
  min,
  openOnMount = false,
  dateOnly = false,
  'aria-invalid': ariaInvalid,
}: DatetimeLocalFieldProps) {
  const emphasized = attention || highlight
  const triggerRef = useRef<HTMLDivElement>(null)
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const labelId = useId()
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (!externalRef) return
    externalRef.current = hiddenInputRef.current
  }, [externalRef, value])

  useEffect(() => {
    if (openOnMount) {
      const t = window.setTimeout(() => setPickerOpen(true), 350)
      return () => window.clearTimeout(t)
    }
  }, [openOnMount])

  const openPicker = useCallback(() => setPickerOpen(true), [])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openPicker()
    }
  }

  return (
    <div className={cn('relative', pickerOpen && 'z-[200]')}>
      <input
        ref={hiddenInputRef}
        type="hidden"
        value={value}
        tabIndex={-1}
        aria-hidden
        readOnly
      />

      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-labelledby={labelId}
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        aria-invalid={ariaInvalid}
        onClick={openPicker}
        onKeyDown={onKeyDown}
        className={cn(
          'relative flex cursor-pointer items-center gap-3',
          emphasized
            ? agendarFieldShellClass
            : 'min-h-[52px] rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2.5 hover:border-accent/40 hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-accent/30',
        )}
      >
        <span
          className={cn(
            emphasized
              ? agendarFieldIconWrapClass
              : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent',
          )}
        >
          <CalendarClock size={18} aria-hidden />
        </span>
        <div className="min-w-0 flex-1 text-left">
          <p
            id={labelId}
            className={cn(
              'text-[10px] font-medium uppercase tracking-wider',
              emphasized ? agendarFieldLabelClass : 'text-text-label',
            )}
          >
            {label}
          </p>
          <p
            className={cn(
              'truncate text-sm font-medium',
              value ? 'text-text-primary' : 'text-text-muted',
            )}
          >
            {value ? formatDisplay(value, dateOnly) : placeholder}
          </p>
        </div>
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-accent">
          Alterar
        </span>
      </div>

      {pickerOpen && (
        <MeetingDatetimePicker
          anchorRef={triggerRef}
          value={value}
          min={min}
          dateOnly={dateOnly}
          onConfirm={(next) => {
            onChange(next)
            setPickerOpen(false)
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}
